import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ClipboardList, CheckCircle2, Loader2, ChevronRight } from "lucide-react";
import { api } from "../App";

// Mirror of calculateSubmittalStatus in Projects.jsx — kept local so this card stays self-contained.
function calculatePlanningStatus(step) {
  if (!step) return "Yet to start";
  if (!step.planned_date && !step.actual_date) return "Yet to start";
  if (step.planned_date && !step.actual_date) return "Planned";
  if (step.actual_date) {
    if (step.step_name === "Back from Approval" || step.step_name.startsWith("Back from Re-approval")) {
      return step.received_date ? "Received" : "Waiting";
    }
    if (step.submission_complete === "Partial") return "Partially submitted";
    return "Submitted";
  }
  return "Yet to start";
}

function calculateSubmittalStatus(submittal) {
  if (submittal.manually_completed) return "Completed";
  if (submittal.hold_enabled) {
    if (submittal.hold_reason === "RFI") return "HOLD due to RFI";
    if (submittal.hold_reason === "Client") return "HOLD by Client";
  }
  const steps = submittal.steps || [];
  const approvalStep = steps.find((s) => s.step_name === "Approval");
  const reapprovalSteps = steps.filter((s) => s.step_name.startsWith("Re-approval"));
  const backFromApprovalStep = steps.find((s) => s.step_name === "Back from Approval");
  const backFromReapprovalSteps = steps.filter((s) => s.step_name.startsWith("Back from Re-approval"));
  const fabricationSteps = steps.filter(
    (s) => s.step_name === "Fabrication" || s.step_name.startsWith("Fabrication ")
  );

  if (approvalStep) {
    const st = calculatePlanningStatus(approvalStep);
    if (st === "Yet to start" || st === "Planned") return "App-Progress";
  }
  for (const r of reapprovalSteps) {
    const st = calculatePlanningStatus(r);
    if (st === "Yet to start" || st === "Planned") return "App-Progress";
  }
  // Check ANY Back-from step (initial or any re-approval cycle) that is Waiting.
  // Bug fix (Feb 03 2026): previously only "Back from Approval" was checked, so a
  // submittal waiting on "Back from Re-approval N" incorrectly showed App-Progress.
  if (backFromApprovalStep && calculatePlanningStatus(backFromApprovalStep) === "Waiting") {
    return "BFA waiting";
  }
  for (const bfr of backFromReapprovalSteps) {
    if (calculatePlanningStatus(bfr) === "Waiting") return "BFA waiting";
  }
  if (fabricationSteps.length > 0) {
    const latestFab = fabricationSteps[fabricationSteps.length - 1];
    if (latestFab.submission_complete === "Full" && calculatePlanningStatus(latestFab) === "Submitted") {
      return "Completed";
    }
    const anyInProg = fabricationSteps.some((f) => {
      const st = calculatePlanningStatus(f);
      return st === "Planned" || st === "Partially submitted";
    });
    if (anyInProg) return "Fab-Progress";
  }
  return "App-Progress";
}

// Mirror of calculateCompletionPercentage in Projects.jsx — keeps this card consistent
// with the Manage Submittals dialog. Reads "live" so Force-Complete and step progress
// always reflect the correct % (the stored submittal.completion_percentage field is
// only updated when users explicitly save via the Manage Submittals dialog).
function calculateCompletionPercentage(submittal, clientApprovalPercent, clientFabricationPercent) {
  // Derive status the same way the badge does — covers manually_completed + step-driven Completed.
  const status = calculateSubmittalStatus(submittal);
  if (status === "Completed") return 100;

  const steps = submittal.steps || [];
  const approvalStep = steps.find((s) => s.step_name === "Approval");
  if (approvalStep) {
    const st = calculatePlanningStatus(approvalStep);
    // Approval not yet submitted → 0%
    if (st === "Yet to start" || st === "Planned") return 0;
  }

  // Sum partial fabrication submissions, if any
  const fabricationSteps = steps.filter(
    (s) => s.step_name === "Fabrication" || s.step_name.startsWith("Fabrication ")
  );
  if (fabricationSteps.length > 0) {
    let totalPartialPct = 0;
    let hasPartial = false;
    for (const fabStep of fabricationSteps) {
      if (fabStep.submission_complete === "Partial" && fabStep.submission_percentage) {
        totalPartialPct += fabStep.submission_percentage;
        hasPartial = true;
      }
    }
    if (hasPartial) {
      const fabricationContribution = (totalPartialPct / 100) * clientFabricationPercent;
      return clientApprovalPercent + fabricationContribution;
    }
  }
  // Approval submitted, no fab progress yet → approval percentage
  return clientApprovalPercent;
}

// Pick the most relevant date for the row based on the status.
function getRelevantDate(submittal, status) {
  if (submittal.manually_completed && submittal.manually_completed_at) {
    return { label: "Force-completed on", date: submittal.manually_completed_at };
  }
  const steps = submittal.steps || [];
  const approval = steps.find((s) => s.step_name === "Approval");
  const fabSteps = steps.filter((s) => s.step_name === "Fabrication" || s.step_name.startsWith("Fabrication "));
  const latestFab = fabSteps[fabSteps.length - 1];

  if (status === "App-Progress") {
    return approval?.planned_date ? { label: "Approval planned", date: approval.planned_date } : null;
  }
  if (status === "BFA waiting") {
    return approval?.actual_date ? { label: "Submitted on", date: approval.actual_date } : null;
  }
  if (status === "Fab-Progress" || status === "Completed") {
    if (latestFab?.actual_date) return { label: "Fab actual", date: latestFab.actual_date };
    if (latestFab?.planned_date) return { label: "Fab planned", date: latestFab.planned_date };
  }
  return null;
}

function fmtDate(d) {
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
}

const STATUS_BADGE_CLASS = {
  "App-Progress": "bg-blue-100 text-blue-700 border-blue-200",
  "BFA waiting": "bg-yellow-100 text-yellow-700 border-yellow-200",
  "Fab-Progress": "bg-purple-100 text-purple-700 border-purple-200",
  Completed: "bg-green-100 text-green-700 border-green-200",
  "HOLD due to RFI": "bg-orange-100 text-orange-700 border-orange-200",
  "HOLD by Client": "bg-red-100 text-red-700 border-red-200",
};

export default function SubmittalStatusCard({ projectId, project }) {
  const [submittals, setSubmittals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewFC, setViewFC] = useState({ open: false, submittal: null });
  // Default: hide completed submittals so the team focuses on active work.
  const [hideCompleted, setHideCompleted] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Client billing percentages drive partial-fab completion math. Default mirrors
  // the Manage Submittals dialog fallback (Projects.jsx).
  const [clientBilling, setClientBilling] = useState({ approval: 90, fabrication: 10 });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/projects/${projectId}/submittals`);
        if (mounted) setSubmittals(res.data || []);
      } catch {
        if (mounted) setSubmittals([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [projectId]);

  // Fetch the project's client billing percentages so partial-fab math matches the
  // Manage Submittals dialog exactly. Falls back to 90/10 on any failure.
  useEffect(() => {
    let mounted = true;
    if (!project?.client_id) return;
    (async () => {
      try {
        const res = await api.get(`/clients/${project.client_id}`);
        if (mounted) {
          setClientBilling({
            approval: res.data?.billing_approval || 0,
            fabrication: res.data?.billing_fabrication || 0,
          });
        }
      } catch {
        // Keep default 90/10
      }
    })();
    return () => { mounted = false; };
  }, [project?.client_id]);

  // Group submittals by scope name, using project's scope_of_work for friendly names.
  // Apply the "hide completed" filter while bucketing, and drop empty groups.
  const grouped = useMemo(() => {
    const scopes = project?.scope_of_work || [];
    const scopeNameById = Object.fromEntries(scopes.map((s) => [s.id, s.scope_name || s.name || "Scope"]));
    const buckets = new Map();
    for (const s of submittals) {
      if (hideCompleted && calculateSubmittalStatus(s) === "Completed") continue;
      const key = s.scope_of_work_id || "_unassigned";
      const title = scopeNameById[s.scope_of_work_id] || (s.scope_of_work_id ? "Unknown Scope" : "Unscoped");
      if (!buckets.has(key)) buckets.set(key, { title, items: [] });
      buckets.get(key).items.push(s);
    }
    return Array.from(buckets.values()).filter((b) => b.items.length > 0);
  }, [submittals, project, hideCompleted]);

  // Header stats
  const stats = useMemo(() => {
    let completed = 0, onHold = 0;
    for (const s of submittals) {
      const st = calculateSubmittalStatus(s);
      if (st === "Completed") completed += 1;
      if (st.startsWith("HOLD")) onHold += 1;
    }
    return { total: submittals.length, completed, onHold };
  }, [submittals]);

  if (loading) {
    return (
      <Card className="bg-md3-surface-container shadow-md3-1 rounded-md3-lg border-0" data-testid="submittal-status-card-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-md3-primary" />
            Submittal Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-sm text-md3-on-surface-variant">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading submittals…
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-gray-100 shadow-sm rounded-xl bg-white" data-testid="submittal-status-card">
      <CardHeader className={expanded ? "pb-2" : ""}>
        <CardTitle className="text-base font-semibold text-md3-on-surface flex items-center justify-between gap-2 flex-wrap">
          <button type="button" onClick={() => setExpanded((e) => !e)} className="flex items-center gap-2 hover:text-md3-primary transition-colors" data-testid="submittal-status-toggle">
            <ChevronRight className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-90" : ""}`} />
            <ClipboardList className="h-5 w-5 text-md3-primary" />
            Submittal Status
          </button>
          <div className="flex items-center gap-4 flex-wrap">
            {submittals.length > 0 && (
              <span className="text-xs font-normal text-md3-on-surface-variant" data-testid="submittal-status-stats">
                {stats.total} submittal{stats.total === 1 ? "" : "s"} · {stats.completed} completed · {stats.onHold} on hold
              </span>
            )}
            {expanded && submittals.length > 0 && (
              <div className="flex items-center gap-2" data-testid="submittal-hide-completed-row">
                <Label htmlFor="submittal-hide-completed-toggle" className="text-xs font-normal text-md3-on-surface-variant cursor-pointer">
                  Hide completed
                </Label>
                <Switch
                  id="submittal-hide-completed-toggle"
                  checked={hideCompleted}
                  onCheckedChange={setHideCompleted}
                  data-testid="submittal-hide-completed-toggle"
                />
              </div>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      {expanded && <CardContent>
        {submittals.length === 0 ? (
          <p className="text-sm text-md3-on-surface-variant" data-testid="submittal-status-empty">
            No submittals tracked for this project yet.
          </p>
        ) : grouped.length === 0 ? (
          // All submittals are completed and toggle is hiding them
          <p className="text-sm text-md3-on-surface-variant" data-testid="submittal-status-all-completed">
            All submittals are completed.{" "}
            <button
              type="button"
              className="underline text-md3-primary hover:opacity-80"
              onClick={() => setHideCompleted(false)}
              data-testid="submittal-show-completed-link"
            >
              Show completed
            </button>{" "}
            to view them.
          </p>
        ) : (
          <div className="space-y-4">
            {grouped.map((bucket) => (
              <div key={bucket.title}>
                <div className="text-xs font-semibold text-md3-on-surface-variant uppercase tracking-wide mb-2">
                  {bucket.title}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-md3-on-surface-variant border-b border-md3-outline-variant">
                        <th className="py-1.5 pr-2 font-medium">Submittal</th>
                        <th className="py-1.5 px-2 font-medium">Status</th>
                        <th className="py-1.5 px-2 font-medium">Date</th>
                        <th className="py-1.5 pl-2 font-medium text-right">Completion</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bucket.items.map((sub) => {
                        const status = calculateSubmittalStatus(sub);
                        const dateInfo = getRelevantDate(sub, status);
                        const pct = calculateCompletionPercentage(
                          sub,
                          clientBilling.approval,
                          clientBilling.fabrication
                        ).toFixed(1);
                        return (
                          <tr
                            key={sub.id}
                            className="border-b last:border-0 border-md3-outline-variant"
                            data-testid={`submittal-status-row-${sub.id}`}
                          >
                            <td className="py-2 pr-2 font-medium text-md3-on-surface">{sub.submittal_name}</td>
                            <td className="py-2 px-2">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <Badge
                                  variant="outline"
                                  className={`text-xs ${STATUS_BADGE_CLASS[status] || "bg-gray-100 text-gray-700 border-gray-200"}`}
                                >
                                  {status}
                                </Badge>
                                {sub.manually_completed && (
                                  <button
                                    type="button"
                                    onClick={() => setViewFC({ open: true, submittal: sub })}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200"
                                    title="View force-complete note"
                                    data-testid={`submittal-fc-badge-${sub.id}`}
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    Force-Completed
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-xs text-md3-on-surface-variant whitespace-nowrap">
                              {dateInfo ? (
                                <span title={dateInfo.label}>
                                  <span className="text-[10px] uppercase tracking-wide">{dateInfo.label}: </span>
                                  {fmtDate(dateInfo.date)}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="py-2 pl-2 text-right tabular-nums font-medium text-md3-on-surface">
                              {pct}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {hideCompleted && stats.completed > 0 && (
              <p
                className="text-xs text-md3-on-surface-variant pt-2"
                data-testid="submittal-completed-hidden-hint"
              >
                {stats.completed} completed hidden —{" "}
                <button
                  type="button"
                  className="underline text-md3-primary hover:opacity-80"
                  onClick={() => setHideCompleted(false)}
                  data-testid="submittal-show-completed-inline-link"
                >
                  show completed
                </button>{" "}
                to view.
              </p>
            )}
          </div>
        )}
      </CardContent>}

      {/* View Force-Complete Details */}
      <Dialog open={viewFC.open} onOpenChange={(open) => setViewFC((d) => ({ ...d, open }))}>
        <DialogContent className="sm:max-w-md" data-testid="submittal-fc-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-amber-700" />
              Force-Completed
            </DialogTitle>
            <DialogDescription>
              This submittal was marked complete outside the regular workflow.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm py-2">
            <div>
              <div className="text-xs text-md3-on-surface-variant">By</div>
              <div className="font-medium">{viewFC.submittal?.manually_completed_by_name || "Unknown"}</div>
            </div>
            <div>
              <div className="text-xs text-md3-on-surface-variant">When</div>
              <div className="font-medium">
                {viewFC.submittal?.manually_completed_at
                  ? new Date(viewFC.submittal.manually_completed_at).toLocaleString()
                  : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-md3-on-surface-variant">Note</div>
              <div className="font-medium whitespace-pre-wrap bg-md3-surface-container-low p-2 rounded">
                {viewFC.submittal?.manually_completed_note || "—"}
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => setViewFC({ open: false, submittal: null })}
              data-testid="submittal-fc-view-close"
            >Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
